import { Matrix4, Vector3 } from "three";
import type { OBB } from "three/examples/jsm/math/OBB.js";
import {
  LineSegments2,
  LineSegmentsGeometry,
  createFatLineMaterial,
} from "../shape/segment/FatLine2";
import { VOLUME_DEBUG_LINE_WIDTH_PX } from "../util/DebugFlags";

const _obbM4 = new Matrix4();

function boxEdgePositions(hx: number, hy: number, hz: number): number[] {
  const x = Math.max(hx, 1e-6);
  const y = Math.max(hy, 1e-6);
  const z = Math.max(hz, 1e-6);
  const corners = [
    [x, y, z],
    [x, y, -z],
    [x, -y, z],
    [x, -y, -z],
    [-x, y, z],
    [-x, y, -z],
    [-x, -y, z],
    [-x, -y, -z],
  ];
  const edges: Array<[number, number]> = [
    [0, 1],
    [2, 3],
    [4, 5],
    [6, 7],
    [0, 2],
    [1, 3],
    [4, 6],
    [5, 7],
    [0, 4],
    [1, 5],
    [2, 6],
    [3, 7],
  ];
  const positions: number[] = [];
  for (const [a, b] of edges) {
    positions.push(...corners[a], ...corners[b]);
  }
  return positions;
}

/** Ekranda ~3px tel. OBB merkez ve dönüşü objenin world/local uzayına yazılır. */
export function createVolumeDebugWire(obb: OBB, color: number): LineSegments2 {
  const geometry = new LineSegmentsGeometry();
  geometry.setPositions(
    boxEdgePositions(obb.halfSize.x, obb.halfSize.y, obb.halfSize.z),
  );
  const material = createFatLineMaterial({
    color,
    linewidth: VOLUME_DEBUG_LINE_WIDTH_PX,
    depthTest: false,
    transparent: false,
    alphaToCoverage: false,
  });
  const lines = new LineSegments2(geometry, material);
  lines.position.copy(obb.center);
  _obbM4.setFromMatrix3(obb.rotation);
  lines.quaternion.setFromRotationMatrix(_obbM4);
  lines.name = "obbDebugWireframe";
  lines.frustumCulled = false;
  lines.renderOrder = 9996;
  (lines as unknown as { raycast: () => void }).raycast = () => {};
  return lines;
}

/** Dünya uzayında tek segment. `dashed` karşı yüz uzaklığı için. */
export function createVolumeDebugSegment(
  from: Vector3,
  to: Vector3,
  color: number,
  dashed = false,
): LineSegments2 {
  const geometry = new LineSegmentsGeometry();
  geometry.setPositions([from.x, from.y, from.z, to.x, to.y, to.z]);
  const material = createFatLineMaterial({
    color,
    linewidth: VOLUME_DEBUG_LINE_WIDTH_PX,
    depthTest: false,
    transparent: false,
    alphaToCoverage: false,
    dashed,
    dashSize: dashed ? 0.06 : undefined,
    gapSize: dashed ? 0.04 : undefined,
  });
  const lines = new LineSegments2(geometry, material);
  if (dashed) lines.computeLineDistances();
  lines.name = "collisionSeparationDebug";
  lines.frustumCulled = false;
  lines.renderOrder = 10000;
  (lines as unknown as { raycast: () => void }).raycast = () => {};
  return lines;
}
